import { useMemo, FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { getDate } from 'utils/convertString';
import { useStore } from 'hooks/useRootStore';
import { useDebounce } from 'hooks/useDebounce';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { Button } from 'components/Core/Button';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { AppLoader } from 'components/Core/AppLoader/AppLoader';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { IPopularProductData } from 'stores/popularProducts/types';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
// import { ReactComponent as DeleteIcon } from 'assets/bin_icon.svg';
import { popularProductsTableTranslations } from './translations';
import { PopularProductsTableFiltersForm, PopularProductsTableTransFields } from './types';
import styles from './popularProductsTable.module.scss';

const PopularProductsTable: FC = () => {
  const {
    popularProductsStore: {
      popularProductsList,
      getPopularProductsList,
      changePopularProductsStatus,
      setPopularProductData,
      // deletePopularProduct,
      changePopularProductPositions,
    },
    mainStore: { user },
    popUpStore: { openPopUp, closePopUp, setOrganiserPopUpImages },
    alertStore: { showAlert },
  } = useStore();

  const [filtered, setFiltered] = useState<IPopularProductData[]>([]);

  const navigate = useNavigate();

  const { t } = useMyTranslations<PopularProductsTableTransFields>([popularProductsTableTranslations.name]);

  const { mutate, isLoading: isFetching } = useMutation(getPopularProductsList, {
    mutationKey: 'getPopularProductsList',
    retry: false,
  });

  // const { mutate: deleteMutate, isLoading: isDeleting } = useMutation(deletePopularProduct, {
  //   mutationKey: 'deletePopularProduct',
  //   onSuccess: () => {
  //     showAlert(t('deleteSuccess'), 'success');
  //     closePopUp();
  //     mutate();
  //   },
  //   retry: false,
  // });

  const { mutate: changePositionMutate, isLoading: isChangingPosition } = useMutation(changePopularProductPositions, {
    mutationKey: 'changePopularProductPositions',
    onSuccess: () => {
      showAlert(t('changePositionSuccess'), 'success');
      closePopUp();
    },
    retry: false,
  });

  useEffect(() => {
    mutate();
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<PopularProductsTableFiltersForm>();

  const { watch } = useFormMethods;
  const searchD = useDebounce(watch('search'));

  useEffect(() => {
    setFiltered(
      searchD
        ? popularProductsList?.filter((item) => item.name.toLowerCase().includes(searchD.toLowerCase())) || []
        : popularProductsList || []
    );
  }, [searchD, popularProductsList]);

  const onEditClick = useCallback((item: IPopularProductData) => {
    setPopularProductData(item);
    navigate(`${item.id}`);
  }, []);

  const columns: Column<IPopularProductData>[] = useMemo(
    () => [
      {
        Header: '#',
        id: 'position',
        accessor: ({ position }) => position,
        width: 2,
      },
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original,
            original: { imageUrl, name },
          },
        }: CellProps<IPopularProductData>) => (
          <button className={styles.avatar} onClick={() => onEditClick(original)} disabled={isChangingPosition}>
            <AvatarIcon avatar={imageUrl} name='popular product image' size={56} styleProps={{ borderRadius: 8 }} />
            <p className={styles.name}>{name}</p>
          </button>
        ),
        accessor: ({ name }) => name,
        width: 60,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IPopularProductData>) => (
          <StatusCell id={id} status={status} changeStatus={changePopularProductsStatus} />
        ),
        accessor: ({ status }) => status,
        width: 30,
      },
      {
        Header: t('createdAt'),
        id: 'createdAt',
        accessor: ({ createdAt }) => getDate(createdAt),
        width: 40,
      },
      {
        Header: t('updatedAt'),
        id: 'updatedAt',
        accessor: ({ updatedAt }) => (updatedAt ? getDate(updatedAt) : '-'),
        width: 40,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IPopularProductData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button
                  className={`${styles.editBtn}`}
                  onClick={() => onEditClick(original)}
                  disabled={isChangingPosition}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message={t('edit')}
              place='top'
            />
            {/* <ComponentWithTooltip
              component={
                <button
                  className={`${styles.editBtn}`}
                  onClick={() =>
                    openPopUp({
                      title: t('confirmationTitle'),
                      description: t('approve-text'),
                      buttonLabel: t('confirm'),
                      formName: 'confirmation',
                      cb: () => deleteMutate(original.id),
                    })
                  }
                  disabled={isDeleting || isChangingPosition}>
                  <DeleteIcon />
                </button>
              }
              name='deleteIcon'
              message={t('confirm').toLowerCase()}
              place='top'
            /> */}
          </div>
        ),
        width: 30,
        disableSortBy: true,
      },
    ],
    [filtered, isChangingPosition]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <FormProvider {...useFormMethods}>
        <div className={styles.search}>
          <ControlledInput
            name='search'
            classNameProp={styles.searchInput}
            placeholder={t('search')}
            isRemoveMargin
            disabled={!popularProductsList}
          />
          <Button
            title={t('organise')}
            onClick={() => {
              if (!popularProductsList || popularProductsList.length === 0) return;
              setOrganiserPopUpImages(popularProductsList.filter((item) => item.status === 'Active'));
              setTimeout(() => {
                openPopUp({
                  formName: 'organiserImages',
                  // title: t('organiserImagesTitle'),
                  title: 'Organiser Popular Products Images',
                  cb: (data: IPopularProductData[]) => changePositionMutate(data),
                });
              }, 300);
            }}
            stylesProp={{ padding: '8px 36px' }}
          />
          <Button title={t('addBtn')} onClick={() => navigate('add')} stylesProp={{ padding: '8px 36px' }} />
        </div>
      </FormProvider>
      {isFetching ? (
        <AppLoader />
      ) : popularProductsList && popularProductsList.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filtered]}
          simpleList
          isPaginated={popularProductsList.length > PAGE_SIZE}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(PopularProductsTable);
